/**
 * Service pour gérer l'intégration avec l'API Google Maps et Places
 */

class GoogleMapsService {
  constructor() {
    this.isLoaded = false;
    this.isLoading = false;
    this.apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    this.loadPromise = null;
  }

  /**
   * Charge l'API Google Maps avec les librairies Places
   */
  loadGoogleMapsAPI() {
    // Si déjà chargé, retourner une promesse résolue
    if (this.isLoaded && window.google) {
      return Promise.resolve();
    }

    // Si en cours de chargement, retourner la promesse existante
    if (this.isLoading && this.loadPromise) {
      return this.loadPromise;
    }

    // Vérifier si la clé API est disponible
    if (!this.apiKey) {
      console.warn('Clé API Google Maps manquante. Ajoutez REACT_APP_GOOGLE_MAPS_API_KEY dans votre .env');
      return Promise.reject(new Error('API key manquante'));
    }

    this.isLoading = true;

    this.loadPromise = new Promise((resolve, reject) => {
      // Vérifier si l'API n'est pas déjà chargée
      if (window.google && window.google.maps) {
        this.initializeServices();
        this.isLoaded = true;
        this.isLoading = false;
        resolve();
        return;
      }

      // Créer un callback global unique
      const callbackName = `initGoogleMaps_${Date.now()}`;
      window[callbackName] = () => {
        try {
          this.initializeServices();
          this.isLoaded = true;
          this.isLoading = false;

          // Nettoyer le callback
          delete window[callbackName];
          resolve();
        } catch (error) {
          this.isLoading = false;
          reject(error);
        }
      };

      // Charger le script
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${this.apiKey}&libraries=places,geometry,elevation&callback=${callbackName}`;
      script.async = true;
      script.defer = true;

      script.onerror = () => {
        this.isLoading = false;
        delete window[callbackName];
        reject(new Error('Erreur lors du chargement de l\'API Google Maps'));
      };

      document.head.appendChild(script);
    });

    return this.loadPromise;
  }

  /**
   * Initialise les services Google Maps
   */
  initializeServices() {
    if (!window.google || !window.google.maps) {
      throw new Error('API Google Maps non disponible');
    }

    // Initialiser le service AutocompleteService
    window.googleAutocompleteService = new window.google.maps.places.AutocompleteService();

    // Initialiser le service PlacesService
    window.googlePlacesService = new window.google.maps.places.PlacesService(
      document.createElement('div')
    );

    // Initialiser le service ElevationService
    window.googleElevationService = new window.google.maps.ElevationService();

    // Initialiser le service Geocoder pour reverse geocoding
    window.googleGeocoderService = new window.google.maps.Geocoder();

    console.log('Services Google Maps initialisés avec succès');
  }

  /**
   * Vérifie si l'API est prête à être utilisée
   */
  isAPIReady() {
    return this.isLoaded &&
           window.google &&
           window.google.maps &&
           window.googleAutocompleteService &&
           window.googlePlacesService &&
           window.googleElevationService &&
           window.googleGeocoderService;
  }

  /**
   * Récupère l'altitude d'une position donnée
   * @param {number} lat - Latitude
   * @param {number} lng - Longitude
   * @returns {Promise<number>} Altitude en mètres
   */
  getElevation(lat, lng) {
    return new Promise((resolve, reject) => {
      if (!this.isAPIReady()) {
        reject(new Error('Services Google Maps non initialisés'));
        return;
      }

      const locations = [{ lat: lat, lng: lng }];

      window.googleElevationService.getElevationForLocations({
        locations: locations
      }, (results, status) => {
        if (status === window.google.maps.ElevationStatus.OK && results && results[0]) {
          resolve(results[0].elevation);
        } else {
          console.warn('Erreur API Google Elevation:', status);
          resolve(0); // Valeur par défaut si l'altitude n'est pas disponible
        }
      });
    });
  }

  /**
   * Récupère l'adresse réelle à partir de coordonnées GPS (reverse geocoding)
   * @param {number} lat - Latitude
   * @param {number} lng - Longitude
   * @returns {Promise<Object>} Données d'adresse formatées
   */
  getRealAddressFromCoordinates(lat, lng) {
    return new Promise((resolve, reject) => {
      if (!this.isAPIReady()) {
        reject(new Error('Services Google Maps non initialisés'));
        return;
      }

      const latlng = { lat: lat, lng: lng };

      window.googleGeocoderService.geocode({ location: latlng }, (results, status) => {
        if (status === 'OK' && results && results.length > 0) {
          // Chercher une adresse précise (street_address ou premise)
          const addressResult = results.find(result =>
            result.types.includes('street_address') ||
            result.types.includes('premise')
          ) || results[0];

          if (addressResult) {
            const addressComponents = addressResult.address_components;

            // Extraire les composants selon le format standard
            const streetNumber = this.getAddressComponent(addressComponents, 'street_number') || '';
            const streetName = this.getAddressComponent(addressComponents, 'route') || '';
            const postalCode = this.getAddressComponent(addressComponents, 'postal_code') || '';
            const city = this.getAddressComponent(addressComponents, 'locality') ||
                        this.getAddressComponent(addressComponents, 'administrative_area_level_2') || '';
            const country = this.getAddressComponent(addressComponents, 'country') || '';

            // Format standardisé : "num de la rue, nom de la rue, code postal, nom de la ville, nom du pays"
            const formattedAddress = [
              streetNumber,
              streetName,
              postalCode,
              city,
              country
            ].filter(component => component.trim()).join(', ');

            resolve({
              formatted_address: formattedAddress,
              street_number: streetNumber,
              street_name: streetName,
              postal_code: postalCode,
              city: city,
              country: country,
              full_google_address: addressResult.formatted_address
            });
          } else {
            resolve(null);
          }
        } else {
          console.warn('Erreur Geocoding API:', status);
          resolve(null);
        }
      });
    });
  }

  /**
   * Extrait un composant spécifique d'une adresse Google
   * @param {Array} components - Composants d'adresse Google
   * @param {string} type - Type de composant recherché
   * @returns {string} Valeur du composant
   */
  getAddressComponent(components, type) {
    const component = components.find(c => c.types.includes(type));
    return component ? component.long_name : '';
  }

  /**
   * Retourne l'instance singleton
   */
  static getInstance() {
    if (!GoogleMapsService.instance) {
      GoogleMapsService.instance = new GoogleMapsService();
    }
    return GoogleMapsService.instance;
  }
}

// Export de l'instance singleton
const googleMapsService = GoogleMapsService.getInstance();
export default googleMapsService;

// Export des méthodes pour faciliter l'usage
export const loadGoogleMapsAPI = () => googleMapsService.loadGoogleMapsAPI();
export const isGoogleMapsReady = () => googleMapsService.isAPIReady();
export const getElevation = (lat, lng) => googleMapsService.getElevation(lat, lng);
export const getRealAddressFromCoordinates = (lat, lng) => googleMapsService.getRealAddressFromCoordinates(lat, lng);